// IE 2026 11 03 / ODOO-350449 (заказ Игоря: агрегат сигналов кругом в кнопке; проблемы подсказки в одну строку, клик — форма ячейки)
// Чистая логика трёх доработок вида ячеек «Сигнали» (utils/cellViews/signals.js) в «Балансі постачання та попиту» (AttrGrid.vue):
// 1) агрегат: круговая диаграмма в кнопке «Сигнали» по всем сигналам, видимым в отчёте (строки после фильтров и раскрытия × колонки периода) — «как будто
//    агрегированная ячейка»: те же виды и цвета, что у кругов ячеек (SIGNALS_ORDER / SIGNALS_COLORS); подсказка по Alt / Show tooltips — таблица видов
//    (цвет, название, число, доля, итог). Выключатель fe.ux.signalsPieAggregate (дефолт ВКЛ);
// 2) проблемы подсказки ячейки — одна строка на проблему, обрезка «…», полный текст по наведению только у обрезанной строки (scrollWidth > clientWidth),
//    клик по строке — форма ячейки на этой проблеме. Выключатель fe.ux.cellTipProblemsOneLine (дефолт ВКЛ);
// 3) мгновенная подсказка: до ответа cell_passport строки строятся из сигналов ячейки (то, что рисует её круг) — бейдж оценки и название вида; ответ story
//    дополняет те же строки на месте цепочкой «проблема → решение»; ответ кешируется по ячейке и версии данных отчёта. Выключатель fe.ux.signalTipInstant (дефолт ВКЛ).
// Тесты — tests/unit/signalsAggregate.test.mjs
import { t as tI18n } from '@/i18n'
import { SIGNALS_ORDER, SIGNALS_COLORS, SIGNALS_TITLES } from '@/utils/cellViews/signals'
import { signalTip_rowMatches } from '@/utils/signalTipProblems'

export const SIGNALS_PIE_FLAG = 'fe.ux.signalsPieAggregate'
export const TIP_ONE_LINE_FLAG = 'fe.ux.cellTipProblemsOneLine'
export const TIP_INSTANT_FLAG = 'fe.ux.signalTipInstant'

const str = (v) => (v === null || v === undefined ? '' : String(v)).trim()
const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;')
const tf = (t) => (typeof t === 'function' ? t : tI18n)

// выключатель: читатель настроек (key, default) → значение; нет читателя / ошибка — ВКЛ
export function signalsAgg_flagOn(setting, key) {
    if (typeof setting !== 'function') return true
    try { return setting(key, true) !== false } catch (e) { return true }
}

// ---- 1. агрегат ----
// списки видов сигналов ячеек (по списку на ячейку, повторы внутри ячейки не считаются) → { counts: { вид: число ячеек }, cells: ячеек с сигналом, sum: сумма по видам }
export function signalsAgg_count(kindLists) {
    const counts = {}
    let cells = 0, sum = 0
    for (const list of (Array.isArray(kindLists) ? kindLists : [])) {
        const kinds = new Set((Array.isArray(list) ? list : []).map(str).filter(Boolean))
        if (!kinds.size) continue
        cells++
        for (const k of kinds) { counts[k] = (counts[k] || 0) + 1; sum++ }
    }
    return { counts, cells, sum }
}

// агрегат → модель диаграммы: строки в порядке приоритета вида (неизвестные виды — в конце), доля = число вида / сумма по видам (сумма долей — 100 %),
// conic — фон круга (conic-gradient) секторами тех же цветов. Сигналов нет — null (диаграммы нет)
export function signalsAgg_model(agg, t = tI18n) {
    const tt = tf(t)
    const counts = agg && agg.counts && typeof agg.counts === 'object' ? agg.counts : {}
    const sum = Object.values(counts).reduce((a, n) => a + (Number(n) > 0 ? Number(n) : 0), 0)
    if (!sum) return null
    const keys = SIGNALS_ORDER.filter(k => counts[k] > 0).concat(Object.keys(counts).filter(k => counts[k] > 0 && !SIGNALS_ORDER.includes(k)).sort())
    const rows = keys.map(k => ({ kind: k, title: tt(SIGNALS_TITLES[k] || k), color: SIGNALS_COLORS[k] || '#9e9e9e', count: Number(counts[k]), share: Number(counts[k]) / sum }))
    let acc = 0
    const stops = rows.map(r => {
        const a = acc * 100, b = (acc + r.share) * 100
        acc += r.share
        return `${r.color} ${+a.toFixed(3)}% ${+b.toFixed(3)}%`
    })
    const conic = rows.length === 1 ? rows[0].color : `conic-gradient(${stops.join(', ')})`
    return { rows, total: sum, cells: Number(agg.cells) || 0, conic, sig: rows.map(r => `${r.kind}:${r.count}`).join(',') }
}

// доля → «12,5 %» (одна цифра после запятой, кроме целых)
export function signalsAgg_pct(share) {
    const p = Math.round((Number(share) || 0) * 1000) / 10
    return `${Number.isInteger(p) ? p : p.toFixed(1)} %`
}

// подсказка диаграммы: заголовок, ячеек с сигналами, таблица видов (цвет, название, число, доля), итог. Нет модели — ''
export function signalsAgg_tipHtml(model, t = tI18n, fmt = (n) => String(n)) {
    const tt = tf(t)
    if (!model || !Array.isArray(model.rows) || !model.rows.length) return ''
    const f = typeof fmt === 'function' ? fmt : (n) => String(n)
    const rows = model.rows.map(r => `<div class="hm-tip-k"><span class="sig-pie-dot" style="background:${esc(r.color)}"></span><span class="hm-tip-kt">${esc(r.title)}</span></div>`
        + `<div class="hm-tip-v">${esc(f(r.count))}</div><div class="hm-tip-v">${esc(signalsAgg_pct(r.share))}</div>`).join('')
    return `<div class="hm-tip hm-tip-g cell-pie-tip sig-pie-tip">`
        + `<div class="hm-tip-head"><div class="hm-tip-title">${esc(tt('Signals in the report'))}</div>`
        + `<div class="hm-tip-sub">${esc(tt('Visible rows and all period columns; cells with signals'))}: ${esc(f(model.cells))}</div></div>`
        + `<div class="hm-tip-sec"><div class="hm-tip-cap">${esc(tt('Signals'))}</div><div class="hm-tip-cap sig-pie-cap">${esc(tt('Count'))}</div><div class="hm-tip-cap sig-pie-cap">${esc(tt('Share'))}</div>${rows}`
        + `<div class="hm-tip-k sig-pie-total"><span class="hm-tip-kt">${esc(tt('Total'))}</span></div><div class="hm-tip-v sig-pie-total">${esc(f(model.total))}</div><div class="hm-tip-v sig-pie-total">100 %</div></div>`
        + `</div>`
}

// ---- 2. строка проблемы обрезана: размеры элемента (или сам элемент) → true, если текст шире строки ----
export function signalsAgg_isTruncated(el) {
    if (!el) return false
    const sw = Number(el.scrollWidth), cw = Number(el.clientWidth)
    return Number.isFinite(sw) && Number.isFinite(cw) && cw > 0 && sw > cw + 1
}

// ---- 3. мгновенная подсказка ----
// вид сигнала → оценка бейджа (те же классы, что у бейджей формы: dropdown-story-badge is-bad / is-warning); вид без оценки — без бейджа
const SIGNAL_GRADE = {
    uncovered: 'bad', 'over-100': 'bad', bad: 'bad', expired: 'bad',
    excess: 'warning', 'over-max': 'warning', warning: 'warning',
}
const GRADE_TEXT = { bad: 'Bad', warning: 'Warning' }

// виды сигналов ячейки (из круга ячейки) → строки подсказки той же формы, что signalTip_rows: ключ 'sig:<вид>', бейдж оценки, название вида;
// opts.share — доля значения (over-100: «Over 100 % · 128 %»)
export function signalTip_signalRows(kinds, t = tI18n, opts = {}) {
    const tt = tf(t)
    const seen = new Set(), out = []
    const list = (Array.isArray(kinds) ? kinds : []).map(str).filter(Boolean)
    const ordered = SIGNALS_ORDER.filter(k => list.includes(k)).concat(list.filter(k => !SIGNALS_ORDER.includes(k)))
    for (const k of ordered) {
        if (seen.has(k)) continue
        seen.add(k)
        const grade = SIGNAL_GRADE[k] || ''
        let label = tt(SIGNALS_TITLES[k] || k)
        if (k === 'over-100' && Number(opts.share) > 1) label += ` · ${signalsAgg_pct(opts.share)}`
        out.push({ key: 'sig:' + k, kind: 'signal', signalKind: k, grade, gradeText: grade ? tt(GRADE_TEXT[grade]) : '', gradeCss: grade ? 'is-' + grade : '',
            label, links: [], accepted: false, horizon: '', problemKind: '' })
    }
    return out
}

// слияние: строки сигналов (мгновенные) + строки story (ответ). Строка сигнала заменяется НА МЕСТЕ первой ещё не взятой строкой story того же вида
// (signalTip_rowMatches), не нашлось — остаётся строкой сигнала; оставшиеся строки story — следом в своём порядке. Story нет — строки сигналов как есть
export function signalTip_mergeRows(signalRows, storyRows) {
    const sig = Array.isArray(signalRows) ? signalRows.filter(Boolean) : []
    const story = Array.isArray(storyRows) ? storyRows.filter(Boolean) : []
    if (!story.length) return sig.slice()
    if (!sig.length) return story.slice()
    const used = new Set(), out = []
    for (const s of sig) {
        const i = story.findIndex((r, j) => !used.has(j) && signalTip_rowMatches(r, s.signalKind))
        if (i >= 0) { used.add(i); out.push(story[i]) } else out.push(s)
    }
    story.forEach((r, j) => { if (!used.has(j)) out.push(r) })
    return out
}

// IE 2026 11 03 / ODOO-350449 (замечание Игоря: мгновенная подсказка сигнала явно говорит о подгрузке проблем):
// строка состояния под строками мгновенной подсказки: story ещё в пути ('loading') — предварительные строки сигналов приглушены, «Loading problems…» со спиннером;
// ответ пришёл без проблем или с ошибкой ('empty') — «No problem details»; есть строки story (в том числе из кеша) — '' (итог без строки состояния)
export function signalTip_noteOf(state, storyRows) {
    if (state === 'loading') return 'loading'
    if (state === 'ok' && Array.isArray(storyRows) && storyRows.length) return ''
    return 'empty'
}

// кеш ответов cell_passport подсказки: одна Map на версию данных отчёта (тождество record._doc и версия видов); другая версия (перечитали отчёт) — кеш пуст.
// state — объект-хранилище грида ({ doc, ver, map }); возвращает Map текущей версии
export function signalTip_cacheFor(state, doc, ver = 0, cap = 300) {   // IE 2026 11 03 / ODOO-350449 (Игорь: предзагрузка цепочек сигналов; BE ie-dm7 #1009): cap — потолок кеша (предзагрузка поднимает до двух экранов ячеек)
    const s = state && typeof state === 'object' ? state : {}
    if (!(s.map instanceof Map) || s.doc !== doc || s.ver !== ver) { s.doc = doc; s.ver = ver; s.map = new Map() }
    if (s.map.size > Math.max(300, Number(cap) || 0)) s.map.clear()   // IE 2026 11 03 / ODOO-350449 (Игорь: предзагрузка цепочек сигналов; BE ie-dm7 #1009)
    return s.map
}
// ключ ячейки в кеше: отбор ячейки (where строки и предков, месяц / месяцы периода — то есть строка и колонка) — тот же, что уходит в cell_passport
export function signalTip_cacheKey(params) {
    try { return JSON.stringify(params) } catch (e) { return '' }
}

// ---- IE 2026 11 03 / ODOO-350449 (webscm #128539: круг сигналов дешевле — охват прежний) ----
// Было: на каждый вызов (refreshed / свёртка / смена вида, дебаунс 300 мс) — cellView_of + cellView_build по ВСЕМ развёрнутым строкам × колонкам периода
// (157 мс на 80 × 60), списки видов на ячейку и Set в signalsAgg_count. Стало (выключатель fe.ux.signalsPieCached, дефолт ВКЛ):
//   1) отпечаток версии данных — base (вид, форма, версия ответа, шкала, колонки) + видимые строки (тождество dataItem) + значения ячеек периода; совпал — прежний агрегат;
//   2) не совпал — один проход: виды ячейки берутся из кеша ячейки (WeakMap dataItem → массив по колонкам; годен при том же base, значении и классах строки DL),
//      дорогой kindsOf — только для изменившихся ячеек (свёртка / раскрытие считает лишь новые видимые строки); счёт сразу в counts, без списков и Set;
//   3) расчёт — в простое браузера (signalsAgg_idle), кнопка дорисовывает круг после.
// rows — [{ dataItem, isVisible }], cols — [{ binding, date, measure }], kindsOf(dataItem, col, row) → массив видов либо null
export const SIGNALS_PIE_CACHED_FLAG = 'fe.ux.signalsPieCached'

const ids = new WeakMap()
let idSeq = 0
const objId = (o) => { if (!o || typeof o !== 'object') return 0; let n = ids.get(o); if (!n) { n = ++idSeq; ids.set(o, n) } return n }
const mix = (h, n) => Math.imul(h ^ n, 16777619) >>> 0
function mixStr(h, s) { for (let i = 0; i < s.length; i++) h = mix(h, s.charCodeAt(i)); return h }
function mixVal(h, v) {
    if (typeof v === 'number') return mix(mix(h, 1), Number.isFinite(v) ? (Math.round(v * 1000) | 0) : 7)
    if (v === undefined || v === null || v === '') return mix(h, 2)
    if (typeof v === 'string') return mixStr(mix(h, 3), v)
    if (typeof v === 'boolean') return mix(h, v ? 4 : 5)
    return mix(mix(h, 6), objId(v))
}
const cellVal = (di, c) => { const cell = di[c.date]; return (cell && typeof cell === 'object') ? cell[c.measure] : undefined }
const dlCls = (di, c) => { const d = di._DLRow; return d && d.cells && d.cells[c.binding] ? d.cells[c.binding].cssClasses : undefined }

// отпечаток версии данных круга: base + видимые строки + значения ячеек периода (без аллокаций)
export function signalsAgg_sigOf(rows, cols, base) {
    let h = mixStr(2166136261, String(base ?? ''))
    const R = rows || [], C = cols || []
    h = mix(h, C.length)
    for (let i = 0; i < R.length; i++) {
        const r = R[i], di = r && r.dataItem
        if (!di || typeof di !== 'object' || r.isVisible === false) continue
        h = mix(mix(h, objId(di)), objId(di._DLRow))   // строка DL заменена (новые классы с бэка) — другой отпечаток
        for (let j = 0; j < C.length; j++) h = mixVal(h, cellVal(di, C[j]))
    }
    return h
}

// агрегат с кешем: state — объект-хранилище грида ({ sig, agg, cells }); возвращает { agg (как signalsAgg_count), hit — отпечаток совпал, misses — вызовов kindsOf }
export function signalsAgg_scan(state, rows, cols, base, kindsOf) {
    const st = state && typeof state === 'object' ? state : {}
    const b = String(base ?? '')
    const sig = signalsAgg_sigOf(rows, cols, b)
    if (st.agg && st.sig === sig && st.base === b) return { agg: st.agg, hit: true, misses: 0 }
    if (!(st.cells instanceof WeakMap) || st.base !== b) st.cells = new WeakMap()
    const counts = {}
    let cells = 0, sum = 0, misses = 0
    const R = rows || [], C = cols || []
    for (let i = 0; i < R.length; i++) {
        const r = R[i], di = r && r.dataItem
        if (!di || typeof di !== 'object' || r.isVisible === false) continue
        let m = st.cells.get(di)
        if (!m) { m = new Array(C.length); st.cells.set(di, m) }   // по индексу колонки: набор колонок входит в base
        for (let j = 0; j < C.length; j++) {
            const c = C[j], v = cellVal(di, c), cls = dlCls(di, c)
            let e = m[j]
            if (!e || e.v !== v || e.cls !== cls) {
                let kinds = null
                try { kinds = typeof kindsOf === 'function' ? kindsOf(di, c, r) : null } catch (err) { kinds = null }
                misses++
                e = { v, cls, kinds: Array.isArray(kinds) && kinds.length ? kinds : null }
                m[j] = e
            }
            const ks = e.kinds
            if (!ks) continue
            let any = false
            for (let k = 0; k < ks.length; k++) {
                const x = ks[k]
                if (!x) continue
                let dup = false
                for (let q = 0; q < k; q++) if (ks[q] === x) { dup = true; break }
                if (dup) continue
                counts[x] = (counts[x] || 0) + 1; sum++; any = true
            }
            if (any) cells++
        }
    }
    const agg = { counts, cells, sum }
    st.sig = sig; st.base = b; st.agg = agg
    return { agg, hit: false, misses }
}

// выполнить fn в простое браузера (requestIdleCallback, не позже timeout мс; без него — setTimeout 0); возвращает функцию отмены
export function signalsAgg_idle(fn, timeout = 500, g = (typeof window !== 'undefined' ? window : globalThis)) {
    if (g && typeof g.requestIdleCallback === 'function') {
        const h = g.requestIdleCallback(() => fn(), { timeout })
        return () => { try { g.cancelIdleCallback(h) } catch (e) { /* уже выполнен */ } }
    }
    const h = setTimeout(fn, 0)
    return () => clearTimeout(h)
}
